/**
 * trustVerifier.ts — Checks PWA conditions before scheduling a retreat.
 * Answers: "Will the bell ring?"
 * Task 10 (Session E).
 */

export type TrustCheck = {
  id: string;
  label: string;
  detail: string;
  status: "good" | "caution";
  advice?: string;
};

export type TrustResult = {
  checks: TrustCheck[];
  verifiedAt: number;
};

async function getCharging(): Promise<boolean | null> {
  if (typeof navigator === "undefined" || !("getBattery" in navigator)) return null;
  try {
    const battery = await Promise.race([
      (navigator as any).getBattery() as Promise<{ charging: boolean }>,
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("timeout")), 2000),
      ),
    ]);
    return battery.charging;
  } catch {
    return null;
  }
}

export async function runTrustChecks(): Promise<TrustResult> {
  const isStandalone =
    typeof window !== "undefined" &&
    window.matchMedia?.("(display-mode: standalone)").matches;

  const notificationsGranted =
    typeof Notification !== "undefined"
      ? Notification.permission === "granted"
      : false;

  const charging = await getCharging();

  const checks: TrustCheck[] = [
    {
      id: "installed",
      label: "Installed to home screen",
      detail: "So the app survives a refresh",
      status: isStandalone ? "good" : "caution",
      advice: isStandalone
        ? undefined
        : "Add to home screen for best results",
    },
    {
      id: "notifications",
      label: "Notifications allowed",
      detail: "The bell rings on the lock screen",
      status: notificationsGranted ? "good" : "caution",
      advice: notificationsGranted ? undefined : "Allow notifications in device settings",
    },
    {
      id: "plugged-in",
      label: "Phone is plugged in",
      detail: "The app stays awake the full session",
      status: charging === true ? "good" : "caution",
      advice:
        charging === true
          ? undefined
          : "Plug in before the wake bell · charge overnight if possible",
    },
    {
      id: "dnd",
      label: "Do Not Disturb is set",
      detail: "Nothing else interrupts",
      status: "caution",
      advice: "Enable Do Not Disturb before the retreat begins",
    },
    {
      id: "wake-screen",
      label: "Wake-screen permission",
      detail: "The bell can light the screen",
      status: notificationsGranted && isStandalone ? "good" : "caution",
      advice:
        notificationsGranted && isStandalone
          ? undefined
          : "Requires notifications + home screen install",
    },
    {
      id: "lock-card",
      label: "Lock-screen retreat card",
      detail: "One tap returns you to practice",
      status: isStandalone ? "good" : "caution",
      advice: isStandalone ? undefined : "Install to home screen first",
    },
  ];

  return { checks, verifiedAt: Date.now() };
}
